import { css } from '@emotion/react';
import {
  Avatar,
  color,
  Popover,
  spacing,
  TopBar as OriginalTopBar,
  TopBarProps as OriginalTopBarProps,
  Button
} from '@uniquegood/realworld-studio-design';
import { useCallback, useState } from 'react';

import { onMobile } from '@/styles/responsive';
import { useIsMobile, useRequest } from '@/hooks';
import { helpLink } from '@/utils';
import { Identity } from '@/styles';
import ViewOnAppModal from './components/ViewOnAppModal';
import UserMenu from './components/UserMenu';
import { useEventListener } from '../ActionHandler/hooks';
import Credit from './components/Credit';
import ReleaseManageButton from './components/ReleaseManageButton';
import CopyPlayLinkButton from './components/CopyPlayLinkButton';

export type TopBarType = 'logoOnly' | 'default' | 'operation' | 'mission' | 'release';

export type TopBarProps = Omit<
  OriginalTopBarProps,
  'items' | 'logoMoveUrl' | 'missionButton' | 'profileSrc' | 'userMenu' | 'type'
> & { 
  profileSrc?: string;
  type?: TopBarType;
};

export default function TopBar(props: TopBarProps) {
  const { type, cssStyle, ...restProps } = props;

  const styles = [
    ...(Array.isArray(cssStyle) ? cssStyle : [cssStyle ?? Identity]),
    css`
      height: ${TOP_BAR_HEIGHT};
    `
  ];

  const [showUserMenu, setShowUserMenu] = useState(false);
  const toggleShowUserMenu = useCallback(() => {
    setShowUserMenu((showUserMenu) => !showUserMenu);
  }, []);

  const [isModalOpen, setModalOpen] = useState(false);
  const toggleModal = useCallback(() => {
    setModalOpen((isModalOpen) => !isModalOpen);
  }, []);

  useEventListener('click', () => setShowUserMenu(false));

  const { data: me } = useRequest<MyInfo>('/api/me');

  const isMobile = useIsMobile();

  const AvatarStateStyle = css`
    ${type === 'mission'
      ? `margin-left: ${isMobile ? spacing.margin.xsmall2 : 0};`
      : `margin-left: ${isMobile ? spacing.margin.xsmall2 : 0};`}
  `;

  return (
    <>
      <ViewOnAppModal toggleModal={toggleModal} isOpen={isModalOpen} />
      <OriginalTopBar
        items={[
          {
            content: '매뉴얼',
            icon: 'question_circle_solid',
            url: helpLink.notion.helpCenter,
            external: true
          },
          {
            content: '피드백 보내기',
            icon: 'envelope_solid',
            url: 'https://rwd.to/realworldstudio-feedback',
            external: true
          }
        ]}
        logoMoveUrl="/"
        profileSrc={me?.profileImage}
        {...restProps}
        type={type as OriginalTopBarProps['type']}
        cssStyle={styles}
      >
        {(type === 'operation' || type === 'mission' || type === 'release') && (
          <div css={CreditWrapperStyle}>
            <Credit />
          </div>
        )}
        {(type === 'mission' || type === 'release') && (
          <Button
            cssStyle={css`
              margin-left: ${spacing.margin.small};
              margin-right: ${spacing.margin.large};
            `}
            onClick={toggleModal}
          >
            미리보기
          </Button>
        )}
        {type === 'mission' && (
          <div
            css={css`
              margin-right: ${spacing.margin.small};
            `}
          >
            <CopyPlayLinkButton />
          </div>
        )}
        {(type === 'operation' || type === 'mission') && (
          <div
            css={css`
              margin-right: ${spacing.margin.large};
            `}
          >
            <ReleaseManageButton />
          </div>
        )}
        {!(type === 'logoOnly') && (
          <Popover
            active={showUserMenu}
            activator={
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleShowUserMenu();
                }}
                css={[AvatarStyle, AvatarStateStyle]}
              >
                <Avatar source={me?.profileImage} size={isMobile ? 'small' : 'medium'} />
              </button>
            }
            onClose={() => {
              // 팝오버를 열때에도 호출이 되어서 빈 함수로 둠
              // setShowUserMenu(false);
            }}
          >
            <UserMenu onClose={toggleShowUserMenu} cssStyle={UserMenuLayout} />
          </Popover>
        )}
      </OriginalTopBar>
    </>
  );
}

export const TOP_BAR_HEIGHT_NUMBER = 58;
export const TOP_BAR_HEIGHT = `${TOP_BAR_HEIGHT_NUMBER}px`;

const AvatarStyle = css`
  border: none;
  background: none;
  cursor: pointer;
  vertical-align: middle;
`;

const CreditWrapperStyle = css`
  order: -1;
  margin-right: ${spacing.margin.large};
`;

const UserMenuLayout = css`
  box-shadow: 0px 2px 30px rgba(0, 0, 0, 0.2);
  width: 393px;

  ${onMobile} {
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    background-color: ${color.surface_default_default};
    width: 100%;
    height: 100%;

    && {
      box-shadow: none;
    }
  }
`;
